import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import {
  Add,
  DeleteOutlineOutlined,
  EditRounded,
  LinkRounded,
  LaunchRounded,
  PaletteRounded,
  SaveRounded,
  OpenInNewRounded,
} from '@mui/icons-material'
import { GlobalContext } from '../../../App'
import BigModal from '../../../components/Modals/BigModal'
import SmallModal from '../../../components/Modals/SmallModal'

const DEFAULT_QUICK_LINK_COLOR = '#061a9a'
const createBlankQuickLink = () => ({ color: DEFAULT_QUICK_LINK_COLOR })
const getContrastTextColor = (color) => {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return '#ffffff'

  const red = parseInt(color.slice(1, 3), 16)
  const green = parseInt(color.slice(3, 5), 16)
  const blue = parseInt(color.slice(5, 7), 16)
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255

  return luminance > 0.6 ? '#1f2933' : '#ffffff'
}

const QuickLinks = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [quickLinks, setQuickLinks] = useState([])
  const [quickLinkForm, setQuickLinkForm] = useState(null)
  const [editingQuickLinkId, setEditingQuickLinkId] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const { setToast } = useContext(GlobalContext)

  const modalOpen = !!quickLinkForm
  const isEditing = !!editingQuickLinkId

  const fetchQuickLinks = () => {
    setLoading(true)
    API.get('/settings?select=customQuickLinks')
      .then((res) => {
        if (res.status === 200) {
          setQuickLinks(res.data.customQuickLinks)
        } else setFetchError(true)
      })
      .catch((e) => {
        console.error(e.message)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => fetchQuickLinks(), [])

  const handleDelete = () => {
    API.put('/site-config', {
      customQuickLinks: quickLinks.filter((x) => x._id !== confirmDelete._id),
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Quick Link Deleted' })
        setConfirmDelete(null)
        fetchQuickLinks()
      }
    })
  }

  const openCreateModal = () => {
    setEditingQuickLinkId(null)
    setQuickLinkForm(createBlankQuickLink())
  }

  const openEditModal = (link) => {
    setEditingQuickLinkId(link?._id)
    setQuickLinkForm({
      ...link,
      color: link?.color || DEFAULT_QUICK_LINK_COLOR,
    })
  }

  const closeQuickLinkModal = () => {
    setEditingQuickLinkId(null)
    setQuickLinkForm(null)
  }

  const handleSubmit = () => {
    const title = quickLinkForm?.title?.trim()
    const url = quickLinkForm?.url?.trim()

    if (
      quickLinks.some(
        (x) =>
          x.title === title && String(x._id) !== String(editingQuickLinkId),
      )
    ) {
      setToast({
        severity: 'error',
        message: 'A quick link with this name already exists',
      })
      return
    }

    const quickLinkToSave = {
      ...quickLinkForm,
      title,
      url,
      color: quickLinkForm.color || DEFAULT_QUICK_LINK_COLOR,
    }

    const updatedQuickLinks = isEditing
      ? quickLinks.map((link) =>
          String(link._id) === String(editingQuickLinkId)
            ? quickLinkToSave
            : link,
        )
      : [...quickLinks, quickLinkToSave]

    API.put('/site-config', {
      customQuickLinks: updatedQuickLinks,
    }).then((res) => {
      if (res.status === 200) {
        setToast(isEditing ? 'Quick Link Updated' : 'New Quick Link Added')
        closeQuickLinkModal()
        fetchQuickLinks()
      } else {
        setToast({
          severity: 'error',
          message: 'Unable to create Quick Link. Please try again later.',
        })
      }
    })
  }

  return (
    <Stack gap={1}>
      {loading && <LinearProgress />}
      <Stack
        gap={{ xs: 0.5, lg: 1 }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="start"
      >
        <Stack>
          <Typography variant="title2">Quick Links</Typography>
          <Typography variant="caption">
            Quick links are external URLs that appear on your staff's home page
          </Typography>
        </Stack>
        <Button
          onClick={openCreateModal}
          variant="contained"
          color="anBlue"
          startIcon={<Add />}
        >
          Add New
        </Button>
      </Stack>
      <List sx={{ p: 0, m: 0 }}>
        {quickLinks.map((link) => (
          <ListItem
            key={link?._id}
            disableGutters
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 2,
              mb: 0.75,
              border: '1px solid',
              transition: '0.15s ease',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'text.primary',
              },
            }}
            secondaryAction={
              <Stack direction="row" gap={0.5}>
                <Tooltip title="Open link">
                  <IconButton
                    size="small"
                    onClick={() =>
                      window.open(link?.url, '_blank', 'noopener,noreferrer')
                    }
                  >
                    <LaunchRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEditModal(link)}>
                    <EditRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDelete(link)}
                  >
                    <DeleteOutlineOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          >
            <Box sx={{ pr: 8, width: '100%' }}>
              <Stack direction="row" alignItems="center" gap={1.25}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: link?.color || DEFAULT_QUICK_LINK_COLOR,
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primary={link?.title}
                  secondary={link?.url}
                  primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    sx: { opacity: 0.75 },
                  }}
                />
              </Stack>
            </Box>
          </ListItem>
        ))}
      </List>
      <BigModal
        open={modalOpen}
        onClose={closeQuickLinkModal}
        style={{ width: { xs: '92%', md: 760 }, maxWidth: 760, p: 0 }}
      >
        <Stack
          gap={0}
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack gap={0.25}>
              <Typography variant="title3" display="block">
                {isEditing ? 'Edit Quick Link' : 'Add Quick Link'}
              </Typography>
              <Typography variant="caption" display="block">
                Configure the button shown on your staff home screen
              </Typography>
            </Stack>
          </Box>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={2}
            sx={{ p: 2.5 }}
          >
            <Stack
              gap={1.25}
              sx={{
                width: { xs: '100%', md: 260 },
                p: 2,
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography fontWeight={700}>Preview</Typography>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() =>
                  window.open(
                    quickLinkForm?.url,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                sx={{
                  background: quickLinkForm?.color || DEFAULT_QUICK_LINK_COLOR,
                  borderRadius: 1,
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                  cursor: 'pointer',
                  transition: '0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.22)',
                  },
                }}
                // paddingX={{ xs: 3, lg: 1 }}
                // paddingY={{ xs: 2, lg: 1 }}
              >
                <Stack
                  height="100%"
                  width="100%"
                  justifyContent="space-between"
                  alignItems={{ xs: 'center', lg: 'space-between' }}
                  direction={{ xs: 'column', lg: 'row' }}
                  padding={1}
                >
                  <SvgIcon
                    component={OpenInNewRounded}
                    sx={{
                      width: '1.5rem',
                      height: '1.5rem',
                      color: getContrastTextColor(
                        quickLinkForm?.color || DEFAULT_QUICK_LINK_COLOR,
                      ),
                      order: { xs: 1, lg: 2 },
                      marginTop: { xs: 1, lg: 0 },
                    }}
                  />
                  <Typography
                    fontSize="1rem"
                    color={getContrastTextColor(
                      quickLinkForm?.color || DEFAULT_QUICK_LINK_COLOR,
                    )}
                    textAlign="center"
                    sx={{
                      wordWrap: 'break-word',
                      order: { xs: 2, lg: 1 },
                      marginBottom: { xs: 1, lg: 0 },
                    }}
                  >
                    {quickLinkForm?.title || 'Quick Link'}
                  </Typography>
                </Stack>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                The button will open the saved URL in a new tab from the home
                screen.
              </Typography>
            </Stack>
            <Stack gap={1.5} flex={1}>
              <TextField
                label="Quick Link Name"
                helperText="Name of the button displayed on the home screen"
                value={quickLinkForm?.title ?? ''}
                required
                onChange={(e) =>
                  setQuickLinkForm((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
              />
              <TextField
                label="Quick Link URL"
                helperText="AmbuNet does not check these links"
                value={quickLinkForm?.url ?? ''}
                required
                InputProps={{ startAdornment: <LinkRounded sx={{ mr: 1 }} /> }}
                onChange={(e) =>
                  setQuickLinkForm((prev) => ({ ...prev, url: e.target.value }))
                }
              />
              <Stack gap={0.75}>
                <Typography fontWeight={700}>Button Colour</Typography>
                <Stack direction="row" gap={1} alignItems="center">
                  <Box
                    component="input"
                    type="color"
                    aria-label="Button Colour"
                    value={quickLinkForm?.color ?? DEFAULT_QUICK_LINK_COLOR}
                    onChange={(e) =>
                      setQuickLinkForm((prev) => ({
                        ...prev,
                        color: e.target.value,
                      }))
                    }
                    sx={{
                      width: 72,
                      height: 44,
                      p: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      cursor: 'pointer',
                    }}
                  />
                  <Typography
                    sx={{
                      minWidth: 86,
                      fontFamily: 'monospace',
                      color: 'text.secondary',
                    }}
                  >
                    {quickLinkForm?.color ?? DEFAULT_QUICK_LINK_COLOR}
                  </Typography>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<PaletteRounded />}
                    onClick={() =>
                      setQuickLinkForm((prev) => ({
                        ...prev,
                        color: DEFAULT_QUICK_LINK_COLOR,
                      }))
                    }
                  >
                    Reset
                  </Button>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Choose the home screen button colour
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            justifyContent="flex-end"
            gap={1}
            sx={{
              px: 2.5,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button onClick={closeQuickLinkModal}>Cancel</Button>
            <Button
              type="submit"
              color="anGreen"
              variant="contained"
              startIcon={<SaveRounded />}
            >
              {isEditing ? 'Save Changes' : 'Create Quick Link'}
            </Button>
          </Stack>
        </Stack>
      </BigModal>
      <SmallModal open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <Stack gap={0.5} justifyContent="center">
          <Typography textAlign="center">
            Are you sure you want to delete this Quick Link?
          </Typography>
          <Typography variant="caption" textAlign="center">
            {confirmDelete?.title}
          </Typography>
          <Button
            onClick={() => handleDelete()}
            variant="outlined"
            size="small"
            color="anRed"
            fullWidth
          >
            Delete
          </Button>
          <Button
            onClick={() => setConfirmDelete(null)}
            variant="contained"
            size="small"
            color="anGreen"
            fullWidth
          >
            Cancel
          </Button>
        </Stack>
      </SmallModal>
    </Stack>
  )
}

export default QuickLinks
